<template>
    <section :aria-labelledby="sectionTitleId">
        <h6 :id="sectionTitleId" class="text-sm font-extrabold text-foxy-text">Мови читання</h6>
        <fieldset
            ref="languageFieldset"
            tabindex="-1"
            :aria-describedby="languageDescriptionIds"
            :aria-invalid="membershipError || primaryError ? 'true' : 'false'"
            class="mt-3 min-w-0"
        >
            <legend class="sr-only">Вибір мов читання</legend>
            <p :id="descriptionId" class="text-xs text-foxy-text-secondary">Оберіть мови цього прочитання та позначте основну окремо.</p>

            <p v-if="!languages.length" class="mt-3 rounded-xl border border-dashed border-foxy-border p-3 text-sm text-foxy-text-secondary">
                Мову читання не вказано
            </p>

            <ul v-else class="mt-3 space-y-2" aria-label="Мови цього прочитання">
                <li
                    v-for="language in languages"
                    :key="identity(language.code)"
                    class="flex min-h-12 flex-wrap items-center gap-3 rounded-xl border border-foxy-border bg-foxy-surface/70 px-3 py-2"
                >
                    <label class="flex min-h-11 min-w-0 flex-1 cursor-pointer items-center gap-2 text-sm font-semibold text-foxy-text">
                        <input
                            :ref="`primary-${identity(language.code)}`"
                            type="radio"
                            :name="primaryGroupName"
                            :value="language.code"
                            :checked="isPrimary(language)"
                            :aria-label="`Зробити основною: ${language.label}`"
                            :aria-describedby="primaryError ? primaryErrorId : undefined"
                            :aria-invalid="primaryError ? 'true' : 'false'"
                            @change="selectPrimary(language.code)"
                        />
                        <span>
                            <span class="block">{{ language.label }}</span>
                            <span v-if="isPrimary(language)" class="block text-xs font-normal text-foxy-text-secondary">Основна</span>
                            <span v-if="language.supported === false" class="block text-xs font-normal text-foxy-text-secondary">
                                Збережено з історії
                            </span>
                        </span>
                    </label>
                    <button
                        type="button"
                        class="acc-btn min-h-11 shrink-0 !px-3 !py-2 text-xs"
                        :disabled="saving"
                        :aria-label="`Прибрати ${language.label}`"
                        @click="removeLanguage(language)"
                    >
                        Прибрати
                    </button>
                </li>
            </ul>

            <p v-if="membershipError" :id="membershipErrorId" class="mt-2 text-sm text-foxy-error" role="alert">{{ membershipError }}</p>
            <p v-if="primaryError" :id="primaryErrorId" class="mt-2 text-sm text-foxy-error" role="alert">{{ primaryError }}</p>
        </fieldset>

        <div class="mt-3">
            <div v-if="preferencesState.status === 'loading'" class="text-sm text-foxy-text-secondary" role="status">Завантажуємо доступні мови…</div>
            <div v-else-if="preferencesState.status === 'error'" class="rounded-xl border border-foxy-error/30 p-3" role="alert">
                <p class="text-sm text-foxy-error">{{ preferencesState.error || 'Не вдалося завантажити мови читання.' }}</p>
                <button type="button" class="acc-btn mt-3 min-h-11 text-xs" :disabled="saving" @click="$emit('retry-preferences')">
                    Спробувати ще раз
                </button>
            </div>
            <template v-else>
                <div class="flex flex-wrap gap-2">
                    <button
                        v-if="availableLanguagesToAdd.length > 0"
                        ref="addLanguageButton"
                        type="button"
                        class="acc-btn min-h-11"
                        :aria-expanded="chooserOpen && chooserSource === 'preferences' ? 'true' : 'false'"
                        :aria-controls="chooserId"
                        :disabled="saving || preferencesState.status !== 'loaded'"
                        @click="toggleChooser"
                    >
                        Додати мову
                    </button>
                    <button
                        v-if="allowCatalogSearch && availableCatalogLanguagesToAdd.length > 0"
                        ref="fullCatalogButton"
                        type="button"
                        class="min-h-11 rounded-lg px-3 text-sm font-semibold text-foxy-action underline decoration-transparent underline-offset-4 transition hover:decoration-current focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foxy-action disabled:cursor-not-allowed disabled:opacity-60"
                        :aria-expanded="chooserOpen && chooserSource === 'catalog' ? 'true' : 'false'"
                        :aria-controls="chooserId"
                        :disabled="saving || preferencesState.status !== 'loaded'"
                        @click="openCatalogChooser"
                    >
                        Знайти іншу мову
                    </button>
                </div>
                <p v-if="preferencesState.status === 'loaded' && availableLanguagesToAdd.length === 0" class="mt-2 text-sm text-foxy-text-secondary">
                    Усі мови з вашого списку вже додані.
                </p>

                <div
                    v-if="chooserOpen && availableChooserLanguages.length > 0"
                    :id="chooserId"
                    class="mt-3 rounded-xl border border-foxy-border bg-foxy-surface p-3"
                >
                    <div class="flex items-end gap-2">
                        <div class="min-w-0 flex-1">
                            <label :for="searchId" class="mb-1 block text-xs font-semibold text-foxy-text">
                                {{ chooserSource === 'catalog' ? 'Знайти іншу мову' : 'Знайти мову' }}
                            </label>
                            <input
                                :id="searchId"
                                ref="languageSearch"
                                v-model="search"
                                type="search"
                                class="foxy-input text-sm"
                                placeholder="Наприклад, Польська"
                                autocomplete="off"
                                role="combobox"
                                aria-autocomplete="list"
                                aria-haspopup="listbox"
                                :aria-expanded="chooserOpen ? 'true' : 'false'"
                                :aria-controls="resultsId"
                                :aria-activedescendant="activeOptionId"
                                @keydown.down.prevent="moveActiveOption(1)"
                                @keydown.up.prevent="moveActiveOption(-1)"
                                @keydown.enter.prevent="addActiveLanguage"
                                @keydown.esc.prevent.stop="closeChooser(true)"
                            />
                        </div>
                        <button type="button" class="acc-btn min-h-11 !px-3" @click="closeChooser(true)">Закрити</button>
                    </div>

                    <ul
                        v-if="filteredLanguages.length"
                        :id="resultsId"
                        class="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-lg border border-foxy-border p-1"
                        role="listbox"
                        :aria-label="chooserSource === 'catalog' ? 'Усі підтримувані мови читання' : 'Мови читання з налаштувань'"
                    >
                        <li
                            v-for="(language, index) in filteredLanguages"
                            :id="optionId(language)"
                            :key="identity(language.code)"
                            class="cursor-pointer rounded-lg px-3 py-2.5 text-sm text-foxy-text transition"
                            :class="index === activeIndex ? 'bg-foxy-elevated' : 'hover:bg-foxy-elevated/70'"
                            role="option"
                            :aria-selected="index === activeIndex ? 'true' : 'false'"
                            @mouseenter="activeIndex = index"
                            @mousedown.prevent
                            @click="addLanguage(language)"
                        >
                            {{ language.label }}
                            <span v-if="language.supported === false" class="block text-xs text-foxy-text-secondary">Збережено з історії</span>
                        </li>
                    </ul>
                    <p v-else :id="resultsId" class="mt-2 rounded-lg border border-foxy-border p-3 text-sm text-foxy-text-secondary" role="status">
                        Мову не знайдено.
                    </p>
                </div>
            </template>
        </div>
    </section>
</template>

<script lang="ts">
function cloneLanguages(languages) {
    return (Array.isArray(languages) ? languages : []).map((language) => ({ ...language }));
}

export default {
    name: 'LibraryReadingLanguageEditor',
    props: {
        readingId: { type: [Number, String], required: true },
        languages: { type: Array, default: () => [] },
        primaryLanguageCode: { type: String, default: null },
        canonicalLanguages: { type: Array, default: () => [] },
        preferencesState: { type: Object, default: () => ({ status: 'idle', data: null, error: '' }) },
        fullCatalog: { type: Array, default: () => [] },
        allowCatalogSearch: { type: Boolean, default: false },
        saving: { type: Boolean, default: false },
        errors: { type: Object, default: () => ({ primary: '', membership: '' }) },
    },
    data() {
        return { chooserOpen: false, chooserSource: 'preferences', search: '', activeIndex: 0, localPrimaryError: '' };
    },
    computed: {
        scopeId() {
            return `reading-${this.readingId}`;
        },
        sectionTitleId() {
            return `${this.scopeId}-languages-title`;
        },
        descriptionId() {
            return `${this.scopeId}-language-description`;
        },
        membershipErrorId() {
            return `${this.scopeId}-language-membership-error`;
        },
        primaryErrorId() {
            return `${this.scopeId}-language-primary-error`;
        },
        chooserId() {
            return `${this.scopeId}-language-chooser`;
        },
        searchId() {
            return `${this.scopeId}-language-search`;
        },
        resultsId() {
            return `${this.scopeId}-language-results`;
        },
        primaryGroupName() {
            return `${this.scopeId}-primary-language`;
        },
        membershipError() {
            return this.errors.membership || '';
        },
        primaryError() {
            return this.localPrimaryError || this.errors.primary || '';
        },
        languageDescriptionIds() {
            return [this.descriptionId, this.membershipError ? this.membershipErrorId : '', this.primaryError ? this.primaryErrorId : '']
                .filter(Boolean)
                .join(' ');
        },
        selectableLanguages() {
            const byIdentity = new Map();
            const preferred = this.preferencesState.data?.selected || [];
            preferred.forEach((language) => {
                const key = this.identity(language.code);
                if (!key || byIdentity.has(key)) return;
                byIdentity.set(key, { code: language.code, label: language.label, supported: true });
            });
            this.canonicalLanguages.forEach((language) => {
                const key = this.identity(language.code);
                if (!key || byIdentity.has(key)) return;
                byIdentity.set(key, { ...language });
            });
            return [...byIdentity.values()];
        },
        availableLanguagesToAdd() {
            const selected = new Set(this.languages.map((language) => this.identity(language.code)));
            const preferred = this.preferencesState.data?.selected || [];
            const seen = new Set();
            return preferred
                .filter((language) => {
                    const key = this.identity(language.code);
                    if (!key || seen.has(key) || selected.has(key)) return false;
                    seen.add(key);
                    return true;
                })
                .map((language) => ({ code: language.code, label: language.label, supported: true }));
        },
        availableCatalogLanguagesToAdd() {
            const selected = new Set(this.languages.map((language) => this.identity(language.code)));
            const seen = new Set();
            return (Array.isArray(this.fullCatalog) ? this.fullCatalog : [])
                .filter((language) => {
                    const key = this.identity(language.code);
                    if (!key || seen.has(key) || selected.has(key)) return false;
                    seen.add(key);
                    return true;
                })
                .map((language) => ({ code: language.code, label: language.label, supported: true }));
        },
        availableChooserLanguages() {
            return this.chooserSource === 'catalog' ? this.availableCatalogLanguagesToAdd : this.availableLanguagesToAdd;
        },
        filteredLanguages() {
            const query = this.search.trim().toLocaleLowerCase('uk-UA');
            if (!query) return this.availableChooserLanguages;
            return this.availableChooserLanguages.filter(
                (language) =>
                    String(language.label || '')
                        .toLocaleLowerCase('uk-UA')
                        .includes(query) || this.identity(language.code).includes(query),
            );
        },
        activeOptionId() {
            const language = this.filteredLanguages[this.activeIndex];
            return language ? this.optionId(language) : undefined;
        },
        primarySelectionValid() {
            if (!this.languages.length) return this.primaryLanguageCode === null;
            if (this.languages.length === 1) return this.identity(this.primaryLanguageCode) === this.identity(this.languages[0].code);
            return this.languages.some((language) => this.identity(language.code) === this.identity(this.primaryLanguageCode));
        },
    },
    watch: {
        search() {
            this.activeIndex = 0;
        },
        filteredLanguages(languages) {
            if (this.activeIndex >= languages.length) this.activeIndex = Math.max(0, languages.length - 1);
        },
        availableLanguagesToAdd(languages) {
            if (this.chooserSource !== 'preferences' || languages.length || !this.chooserOpen) return;
            this.closeChooser(false);
            this.$nextTick(() => {
                const firstPrimary = this.$el?.querySelector('input[type="radio"]');
                (firstPrimary || this.$refs.languageFieldset)?.focus();
            });
        },
        availableCatalogLanguagesToAdd(languages) {
            if (this.chooserSource !== 'catalog' || languages.length || !this.chooserOpen) return;
            this.closeChooser(false);
            this.$nextTick(() => {
                const firstPrimary = this.$el?.querySelector('input[type="radio"]');
                (firstPrimary || this.$refs.languageFieldset)?.focus();
            });
        },
    },
    mounted() {
        this.focusInitialControl();
    },
    methods: {
        identity(code) {
            return String(code || '')
                .trim()
                .toLowerCase();
        },
        emitDraft(languages, primaryLanguageCode) {
            this.$emit('update', { languages: cloneLanguages(languages), primaryLanguageCode });
        },
        clearErrors() {
            this.localPrimaryError = '';
            this.$emit('clear-errors');
        },
        addLanguage(language) {
            const key = this.identity(language.code);
            if (!key || this.languages.some((selected) => this.identity(selected.code) === key)) return;
            this.clearErrors();
            const languages = [...cloneLanguages(this.languages), { ...language }];
            const exhaustsChooser = this.availableChooserLanguages.length === 1;
            this.emitDraft(languages, languages.length === 1 ? languages[0].code : this.primaryLanguageCode);
            this.search = '';
            this.activeIndex = 0;
            if (exhaustsChooser) {
                this.closeChooser(false);
                this.$nextTick(() => this.focusPrimary(language.code));
            } else {
                this.$nextTick(() => this.$refs.languageSearch?.focus());
            }
        },
        removeLanguage(language) {
            if (this.saving) return;
            const key = this.identity(language.code);
            const removedPrimary = this.identity(this.primaryLanguageCode) === key;
            const languages = cloneLanguages(this.languages).filter((selected) => this.identity(selected.code) !== key);
            let primary = this.primaryLanguageCode;
            if (!languages.length) primary = null;
            else if (languages.length === 1) primary = languages[0].code;
            else if (removedPrimary || !languages.some((selected) => this.identity(selected.code) === this.identity(primary))) {
                primary = null;
                this.localPrimaryError = 'Оберіть основну мову читання.';
            }
            this.$emit('clear-errors');
            this.emitDraft(languages, primary);
            this.closeChooser(false);
            this.$nextTick(() => {
                const firstPrimary = this.$el?.querySelector('input[type="radio"]');
                (this.$refs.addLanguageButton || this.$refs.fullCatalogButton || firstPrimary || this.$refs.languageFieldset)?.focus();
            });
        },
        selectPrimary(code) {
            this.clearErrors();
            this.emitDraft(this.languages, code);
        },
        toggleChooser() {
            if (this.chooserOpen && this.chooserSource === 'preferences') return this.closeChooser(false);
            if (!this.availableLanguagesToAdd.length) return;
            this.chooserOpen = true;
            this.chooserSource = 'preferences';
            this.search = '';
            this.activeIndex = 0;
            this.$nextTick(() => this.$refs.languageSearch?.focus());
        },
        openCatalogChooser() {
            if (!this.allowCatalogSearch || !this.availableCatalogLanguagesToAdd.length) return;
            if (this.chooserOpen && this.chooserSource === 'catalog') return this.closeChooser(false);
            this.chooserOpen = true;
            this.chooserSource = 'catalog';
            this.search = '';
            this.activeIndex = 0;
            this.$nextTick(() => this.$refs.languageSearch?.focus());
        },
        closeChooser(returnFocus = false) {
            const trigger = this.chooserSource === 'catalog' ? this.$refs.fullCatalogButton : this.$refs.addLanguageButton;
            this.chooserOpen = false;
            this.chooserSource = 'preferences';
            this.search = '';
            this.activeIndex = 0;
            if (returnFocus) this.$nextTick(() => trigger?.focus());
        },
        moveActiveOption(direction) {
            const count = this.filteredLanguages.length;
            if (!count) return;
            this.activeIndex = (this.activeIndex + direction + count) % count;
            this.$nextTick(() => document.getElementById(this.activeOptionId)?.scrollIntoView({ block: 'nearest' }));
        },
        addActiveLanguage() {
            const language = this.filteredLanguages[this.activeIndex];
            if (language) this.addLanguage(language);
        },
        optionId(language) {
            return `${this.scopeId}-language-option-${encodeURIComponent(this.identity(language.code))}`;
        },
        isPrimary(language) {
            return this.identity(language.code) === this.identity(this.primaryLanguageCode);
        },
        focusPrimary(code) {
            const primary = this.$refs[`primary-${this.identity(code)}`];
            const target = Array.isArray(primary) ? primary[0] : primary;
            (target || this.$el?.querySelector('input[type="radio"]') || this.$refs.languageFieldset)?.focus();
        },
        validate() {
            if (this.primarySelectionValid) return true;
            this.localPrimaryError = 'Оберіть основну мову читання.';
            return false;
        },
        resetInteraction() {
            this.localPrimaryError = '';
            this.closeChooser(false);
        },
        focusInitialControl() {
            this.$nextTick(() => {
                const firstPrimary = this.$el?.querySelector('input[type="radio"]');
                (firstPrimary || this.$refs.addLanguageButton || this.$refs.fullCatalogButton || this.$refs.languageFieldset)?.focus();
            });
        },
        focusValidation(kind) {
            this.$nextTick(() => {
                if (kind === 'primary') {
                    const firstPrimary = this.$el?.querySelector('input[type="radio"]');
                    (firstPrimary || this.$refs.languageFieldset)?.focus();
                    return;
                }
                (this.$refs.languageFieldset || this.$refs.addLanguageButton || this.$refs.fullCatalogButton)?.focus();
            });
        },
    },
};
</script>
